import { makeAutoObservable } from 'mobx'

import {
  Favorite,
  FavoriteParams,
  FavoriteRequestData,
  FavoritesApi,
} from 'shared/api/avia/favorites'
import { TicketItem } from 'shared/models/ticket.ts'

interface FavoritesDetail {
  id: string
  searchParams: FavoriteParams
  abortController: AbortController
}

class FavoritesStore {
  favoritesList: Favorite[] = []
  favoritesDetail: TicketItem | null = null
  detailLoading = false
  loading = false

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  async fetchFavorites() {
    try {
      this.loading = true
      const { data } = await FavoritesApi.getFavorites()

      this.favoritesList = data.data
    } catch (error) {
      throw error
    } finally {
      this.loading = false
    }
  }

  async fetchFavoritesDetail({
    id,
    searchParams,
    abortController,
  }: FavoritesDetail) {
    try {
      this.detailLoading = true
      const { data } = await FavoritesApi.getFavoriteDetail(
        id,
        searchParams,
        abortController?.signal,
      )

      this.favoritesDetail = data.data
    } catch (error) {
      throw error
    } finally {
      this.detailLoading = false
    }
  }

  async addFavorite(body: FavoriteRequestData) {
    try {
      const { data } = await FavoritesApi.createFavorite(body)

      this.favoritesList = [...this.favoritesList, data.data]
    } catch (error) {
      throw error
    }
  }

  async removeFavorite(id: string, searchParams: FavoriteParams) {
    try {
      await FavoritesApi.removeFavorite(id, {
        ...searchParams,
      })

      this.favoritesList = this.favoritesList.filter(
        (item) => item.favorite_search_key !== id,
      )
    } catch (error) {
      throw error
    }
  }

  async removeAllFavorites() {
    try {
      await FavoritesApi.removeAllFavorites()

      this.favoritesList = []
    } catch (error) {
      throw error
    }
  }

  setIsNotFavorite(isFavorite: boolean | undefined = false) {
    this.favoritesDetail = {
      ...(this.favoritesDetail as TicketItem),
      is_favorite: isFavorite,
    }
  }

  resetFavoritesDetail() {
    this.favoritesDetail = null
    this.detailLoading = false
  }
}

export const favoritesStore = new FavoritesStore()
